import * as Yup from 'yup';
import model from '../model';
import { lateinLettersAndAllowedSymbols } from '../helpers/onlyEnglishLetters';
import { isEighteenOrMore } from '../helpers/dobCheck';
import { cityRegex, streetRegex } from '../helpers/cityOrStateRegex';
import onlyNumbers from '../helpers/onlyNumbers';
import zipCodeRegex from '../helpers/zipCodeRegex';
import { isEmploymentIncomeSource } from '../helpers/incomeCheck';
import showMarriedFields from '../helpers/marriedStatusCheck';
import validatePhone from '../helpers/validatePhone';
import { getMaxLoanAmount, getMinLoanAmount } from '../helpers/loanAmountValues';
const { formField: { first_name, last_name, email, tcpa, date_of_birth_1i, date_of_birth_2i, date_of_birth_3i, mobile_phone, city, state, zip_code, fullStreet, street_address, street_address2, pay_period, income_type, income_source, income_source_description, employer, work_phone, ssn, drivers_license_number, drivers_license_state, active_military, first_pay_date, second_pay_date, pay_date_after_next, loan_amount, bank_routing_number, bank_account_number, monthly_income, terms, marital_status, spouseFullStreet, spouse_street_address, spouse_address2, spouse_state, spouse_city, spouse_last_name, spouse_first_name, spouse_email, is_same_home_address, spouse_zip } } = model;
export const welcomePageSchema = Yup.object().shape({
    [first_name.name]: Yup.string()
        .matches(lateinLettersAndAllowedSymbols, first_name.requiredErrorMsg)
        .required(first_name.requiredErrorMsg),
    [last_name.name]: Yup.string()
        .matches(lateinLettersAndAllowedSymbols, last_name.requiredErrorMsg)
        .min(2)
        .max(128)
        .required(last_name.requiredErrorMsg),
    [email.name]: Yup.string().email(`${email.invalidErrorMsg}`).required(email.requiredErrorMsg),
    [mobile_phone.name]: Yup.string()
        .required(mobile_phone.requiredErrorMsg)
        .test('is-valid', mobile_phone.requiredErrorMsg, (val) => validatePhone(val)),
    [tcpa.name]: Yup.boolean()
});
export const tcpaPageSchema = Yup.object().shape({
    [tcpa.name]: Yup.boolean()
});
export const personalInfoPageSchema = Yup.object().shape({
    [first_name.name]: Yup.string()
        .matches(lateinLettersAndAllowedSymbols, first_name.requiredErrorMsg)
        .required(first_name.requiredErrorMsg),
    [last_name.name]: Yup.string()
        .matches(lateinLettersAndAllowedSymbols, last_name.requiredErrorMsg)
        .min(2)
        .max(128)
        .required(last_name.requiredErrorMsg),
    [email.name]: Yup.string().email(`${email.invalidErrorMsg}`).required(email.requiredErrorMsg),
    [mobile_phone.name]: Yup.string()
        .required(mobile_phone.requiredErrorMsg)
        .test('is-valid', mobile_phone.requiredErrorMsg, (val) => validatePhone(val))
});
export const homeAddressSchema = Yup.object().shape({
    [fullStreet.name]: Yup.string().when([city.name, state.name, zip_code.name, street_address.name], {
        is: (cityVal, stateVal, zipCodeVal) => !!cityVal && !!stateVal && !!zipCodeVal,
        then: Yup.string(),
        otherwise: Yup.string().required(street_address.requiredErrorMsg)
    }),
    [city.name]: Yup.string().min(2).max(256).matches(cityRegex, city.invalidErrorMsg).required(city.requiredErrorMsg),
    [zip_code.name]: Yup.string()
        .required(`${zip_code.requiredErrorMsg}`)
        .matches(zipCodeRegex, zip_code.requiredErrorMsg),
    [street_address.name]: Yup.string()
        .min(2)
        .max(256)
        .matches(streetRegex, street_address.invalidErrorMsg)
        .test('is-valid', '', function validate(val) {
        if (!val)
            return false;
        const trimmedString = val.replace(/[\s.]+/g, '').toLowerCase();
        if (trimmedString.includes('pobox')
            || trimmedString.includes('pobx')
            || trimmedString.includes('postalofficebox')
            || trimmedString.includes('postalofficebx'))
            return this.createError({ message: 'PO BOX addresses are not allowed' });
        return true;
    })
        .required(street_address.requiredErrorMsg),
    [street_address2.name]: Yup.string().matches(streetRegex, street_address.invalidErrorMsg).nullable(),
    [state.name]: Yup.string().required(state.requiredErrorMsg)
});
export const identitySchema = Yup.object()
    .shape({
    [date_of_birth_1i.name]: Yup.number()
        .min(1900, date_of_birth_1i.invalidErrorMsg)
        .required(date_of_birth_1i.requiredErrorMsg),
    [date_of_birth_2i.name]: Yup.number()
        .min(1, date_of_birth_2i.invalidErrorMsg)
        .max(12, date_of_birth_2i.invalidErrorMsg)
        .required(date_of_birth_2i.requiredErrorMsg),
    [date_of_birth_3i.name]: Yup.number()
        .min(1, date_of_birth_3i.invalidErrorMsg)
        .max(31, date_of_birth_3i.invalidErrorMsg)
        .required(date_of_birth_3i.requiredErrorMsg),
    [drivers_license_number.name]: Yup.string().min(2).max(25).required(drivers_license_number.requiredErrorMsg),
    [drivers_license_state.name]: Yup.string().min(2).max(25).required(drivers_license_state.requiredErrorMsg),
    [ssn.name]: Yup.string()
        .matches(/^[\d-()X]{11}$/, ssn.invalidErrorMsg)
        .required(ssn.requiredErrorMsg),
    [active_military.name]: Yup.string().required(active_military.requiredErrorMsg)
})
    .test('is-valid-dob', '', function validate(value) {
    if (!value || !value.date_of_birth_1i || !value.date_of_birth_2i || !value.date_of_birth_3i)
        return false;
    const isValidMinAge = isEighteenOrMore(new Date(Number(value.date_of_birth_1i), Number(value.date_of_birth_2i) - 1, Number(value.date_of_birth_3i)));
    if (!isValidMinAge) {
        return this.createError({
            message: 'Age requirement: 18+. Please enter a valid year.',
            path: 'date_of_birth_1i'
        });
    }
    return true;
});
export const incomeSchema = Yup.object().shape({
    [income_source.name]: Yup.string().required(income_source.requiredErrorMsg),
    [income_source_description.name]: Yup.string(),
    [employer.name]: Yup.string().test('is-valid', '', function validate(val) {
        if (!val || !val.length) {
            const isEmployment = isEmploymentIncomeSource(this.parent[income_source.name]);
            if (isEmployment)
                return this.createError({ message: employer.requiredErrorMsg });
            return true;
        }
        return true;
    }),
    [income_type.name]: Yup.string().required(income_type.requiredErrorMsg),
    [work_phone.name]: Yup.string().test('is-valid', '', function validate(val) {
        if (!val || !val.length) {
            const isEmployment = isEmploymentIncomeSource(this.parent[income_source.name]);
            if (isEmployment)
                return this.createError({ message: work_phone.requiredErrorMsg });
            return true;
        }
        return true;
    })
});
export const incomeDetailSchema = Yup.object().shape({
    [monthly_income.name]: Yup.string().required(monthly_income.requiredErrorMsg),
    [pay_period.name]: Yup.string().required(pay_period.requiredErrorMsg),
    [first_pay_date.name]: Yup.date().required(first_pay_date.requiredErrorMsg),
    [second_pay_date.name]: Yup.date().required(second_pay_date.requiredErrorMsg),
    [pay_date_after_next.name]: Yup.date().required(pay_date_after_next.requiredErrorMsg)
});
export const bankInfoSchema = Yup.object().shape({
    [loan_amount.name]: Yup.string().test('is-valid', '', function validate(val) {
        const errMsg = loan_amount.requiredErrorMsg
            .replace('{{minLoanAmount}}', getMinLoanAmount(true))
            .replace('{{maxLoanAmount}}', getMaxLoanAmount(true));
        if (val) {
            const value = val.toString().replaceAll(',', '');
            const isValid = Number(value) <= getMaxLoanAmount() && Number(value) >= getMinLoanAmount();
            if (isValid)
                return true;
            return this.createError({ message: errMsg });
        }
        return this.createError({ message: errMsg });
    }),
    [terms.name]: Yup.boolean().oneOf([true], terms.requiredErrorMsg),
    [bank_routing_number.name]: Yup.string()
        .matches(onlyNumbers, bank_routing_number.invalidErrorMsg)
        .min(9, bank_routing_number.requiredErrorMsg)
        .max(10, bank_routing_number.requiredErrorMsg)
        .required(bank_routing_number.requiredErrorMsg),
    [bank_account_number.name]: Yup.string()
        .matches(onlyNumbers, bank_account_number.invalidErrorMsg)
        .min(5, bank_account_number.invalidErrorMsg)
        .max(17, bank_account_number.invalidErrorMsg)
        .required(bank_account_number.requiredErrorMsg)
});
export const spousalSchema = Yup.object().shape({
    [marital_status.name]: Yup.string().required(marital_status.requiredErrorMsg),
    [spouse_first_name.name]: Yup.string().when([marital_status.name], {
        is: (marriedStatus) => showMarriedFields(marriedStatus),
        then: Yup.string()
            .matches(lateinLettersAndAllowedSymbols, spouse_first_name.requiredErrorMsg)
            .required(spouse_first_name.requiredErrorMsg),
        otherwise: Yup.string()
    }),
    [spouse_last_name.name]: Yup.string().when([marital_status.name], {
        is: (marriedStatus) => showMarriedFields(marriedStatus),
        then: Yup.string()
            .matches(lateinLettersAndAllowedSymbols, spouse_last_name.requiredErrorMsg)
            .min(2)
            .max(128)
            .required(spouse_last_name.requiredErrorMsg),
        otherwise: Yup.string()
    }),
    [spouse_email.name]: Yup.string().when([marital_status.name], {
        is: (marriedStatus) => showMarriedFields(marriedStatus),
        then: Yup.string().email(`${spouse_email.invalidErrorMsg}`).required(spouse_email.requiredErrorMsg),
        otherwise: Yup.string()
    }),
    [is_same_home_address.name]: Yup.string().required(is_same_home_address.requiredErrorMsg)
});
export const spousalAddressSchema = Yup.object().shape({
    [spouseFullStreet.name]: Yup.string().when([spouse_street_address.name], {
        is: (streetAddress) => !!streetAddress,
        then: Yup.string(),
        otherwise: Yup.string().required(spouseFullStreet.requiredErrorMsg)
    }),
    [spouse_city.name]: Yup.string().min(2).max(256).required(spouse_city.requiredErrorMsg),
    [spouse_zip.name]: Yup.string()
        .required(`${spouse_zip.requiredErrorMsg}`)
        .matches(zipCodeRegex, spouse_zip.requiredErrorMsg),
    [spouse_street_address.name]: Yup.string().required(spouse_street_address.requiredErrorMsg),
    [spouse_address2.name]: Yup.string().nullable(),
    [spouse_state.name]: Yup.string().required(spouse_state.requiredErrorMsg)
});
